import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Group, Title, Stack, Button, Text, Modal } from "@mantine/core";

import { User } from "@jasper/shared";
import { UseAuth } from "/@/contexts";

import OrderListTable from "./components/OrderListTable";
import "./home.css";
import { useOrdersList } from "./hooks/useOrdersList";
import Pagination from "./components/Pagination";
import SearchBar from "./components/SearchBar";
import { IconFilter } from "@tabler/icons-react";
import AdvancedFilterPanel from "./components/AdvancedFilterPanel/AdvancedFilterPanel";
import { useOrderListFilter } from "./contexts/OrderListFilterContext";
import FiltersDescription from "./components/FiltersDescription/FiltersDescription";
import { useOrderSelection } from "./hooks/useOrderSelection";
import { useOrderActions } from "./hooks/useOrderActions";
import { modals } from "@mantine/modals";
import { ActionsButton } from "./components/ActionsButton";
import { DateTimePicker } from "@mantine/dates";

const Home = () => {
  const navigate = useNavigate();
  const { user } = UseAuth();

  const {
    page,
    setPage,
    statuses: filteredStatuses,
    search,
    setSearch,
    getFilterQuery,
  } = useOrderListFilter();

  const { orders, refetchOrders, totalOrderCount } = useOrdersList({
    filteredStatuses,
    filterQuery: getFilterQuery(),
    search,
    page,
  });

  const {
    toggleOrder,
    toggleSelectPage,
    unselectAll,
    selectAllPages,
    selectedOrders,
    selectedPages,
  } = useOrderSelection();

  const { updateOrdersStatus, syncOrdersFrom3Shape } = useOrderActions({
    onStatusesUpdated: () => {
      unselectAll();
      refetchOrders();
    },
    onOrdersSynced: () => {
      refetchOrders();
    },
  });

  const [isSync3ShapeOrdersModalOpened, setIsSync3ShapeOrdersModalOpened] =
    useState(false);
  const [isAdvancedFilterPanelOpened, setIsAdvancedFilterPanelOpened] =
    useState(false);

  const [syncFromDate, setSyncFromDate] = useState<Date>();
  const [syncToDate, setSyncToDate] = useState<Date>();

  const parseMantineDateValue = (value: string | Date | null | undefined) => {
    if (!value) {
      return undefined;
    }

    return new Date(value);
  };

  return (
    <>
      <Modal
        opened={isSync3ShapeOrdersModalOpened}
        onClose={() => {
          setIsSync3ShapeOrdersModalOpened(false);
          setSyncFromDate(undefined);
          setSyncToDate(undefined);
        }}
        title="Sync Orders from 3Shape"
      >
        <Stack>
          <Group w="100%">
            <DateTimePicker
              w="100%"
              label="From"
              value={syncFromDate ?? null}
              maxDate={syncToDate}
              onChange={value => setSyncFromDate(parseMantineDateValue(value))}
            />
            <DateTimePicker
              w="100%"
              label="To"
              value={syncToDate ?? null}
              defaultValue={new Date()}
              minDate={syncFromDate}
              onChange={value => setSyncToDate(parseMantineDateValue(value))}
            />
          </Group>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                setIsSync3ShapeOrdersModalOpened(false);
                setSyncFromDate(undefined);
                setSyncToDate(undefined);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                syncOrdersFrom3Shape({
                  fromDate: syncFromDate!,
                  toDate: syncToDate!,
                });
                setIsSync3ShapeOrdersModalOpened(false);
                setSyncFromDate(undefined);
                setSyncToDate(undefined);
              }}
              disabled={!syncFromDate || !syncToDate}
            >
              Sync
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Stack gap="md">
        <Group justify="space-between">
          <Title>Orders</Title>
          <ActionsButton
            syncOrdersFrom3Shape={() => {
              setSyncToDate(new Date());
              setIsSync3ShapeOrdersModalOpened(true);
            }}
            user={user as unknown as User}
            filteredStatuses={filteredStatuses}
            updateStatuses={statusToUpdate =>
              modals.openConfirmModal({
                title: "Confirm status updates",
                labels: { confirm: "Update statuses", cancel: "Cancel" },
                children: (
                  <Text size="sm">
                    {`You are about to update the status of ${selectedOrders.length} orders to: ${statusToUpdate}`}
                  </Text>
                ),
                onConfirm: () =>
                  updateOrdersStatus(statusToUpdate, selectedOrders),
              })
            }
          />
        </Group>
        <Group w="100%" gap="xs" align="flex-end">
          <Button
            variant={isAdvancedFilterPanelOpened ? "filled" : "outline"}
            onClick={() =>
              setIsAdvancedFilterPanelOpened(!isAdvancedFilterPanelOpened)
            }
            size="compact"
          >
            <IconFilter size={16} />
          </Button>
          <Box flex="1">
            <SearchBar searchText={search} onSearchChange={setSearch} />
          </Box>
        </Group>

        <Stack gap="xs">
          <FiltersDescription />
          <Group wrap="nowrap" align="flex-start" justify="space-between">
            <Box w={isAdvancedFilterPanelOpened ? "15%" : "0"}>
              <AdvancedFilterPanel
                isAdvancedFilterPanelOpened={isAdvancedFilterPanelOpened}
                user={user as unknown as User}
              />
            </Box>
            <Box w="100%">
              <OrderListTable
                user={user as unknown as User}
                data={orders}
                navigate={(url: string) => {
                  navigate(url, {
                    state: {
                      from: "HOME",
                    },
                  });
                }}
                totalCount={totalOrderCount}
                toggleOrder={toggleOrder}
                toggleSelectPage={toggleSelectPage}
                unselectAll={unselectAll}
                selectAllPages={selectAllPages}
                selectedOrders={selectedOrders}
                selectedPages={selectedPages}
                currentPage={page}
              />
            </Box>
          </Group>
        </Stack>

        <Pagination
          page={page}
          setPage={setPage}
          totalOrderCount={totalOrderCount}
          currentPageOrderCount={orders.length}
        />
      </Stack>
    </>
  );
};

export default Home;
